/**
 * Entry point.
 *
 * i18n is initialised BEFORE the first render rather than with a Suspense fallback,
 * because G6's "a missing key renders the key, loudly" is only useful if the catalogue is
 * present when the first paint happens. A shell that renders raw keys for 200 ms and then
 * corrects itself trains everyone to ignore raw keys.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import { initI18n } from './i18n';
import { applyTheme, useThemeStore } from './lib/theme';
import './styles/index.css';

async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  // The persisted theme choice is applied before the first paint, so there is no
  // light-to-dark flash for a planner who has chosen dark (§9).
  const { choice, resolved } = useThemeStore.getState();
  applyTheme(choice, resolved);

  await initI18n({
    // The pseudo-locale build (G6 test d) is selected by an env flag, never by a URL
    // parameter — it must not be reachable in production by guessing.
    pseudo: import.meta.env['VITE_PSEUDO_LOCALE'] === 'true',
    debug: import.meta.env.DEV,
  });

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
